import {FC, ReactNode} from "react";
import {makeStyles} from "tss-react/mui";
import {Box, Breadcrumbs, Button, ButtonProps, CircularProgress, Typography} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import {Link} from "react-router-dom";

const useStyles = makeStyles()((theme) => ({
	header: {
		display: "flex",
		flexDirection: "column"
	},
	title: {
		fontSize: "34px",
		lineHeight: 1.24
	},
	newTitle: {
		lineHeight: 1.24,
		textTransform: "capitalize",
		fontSize: "34px"
	},
	breadcrumbs: {
		".MuiBreadcrumbs-li": {
			display: "flex",
			flexDirection: "row",
			alignItems: "center"
		},
		".MuiSvgIcon-root": {
			fontSize: "1.1em",
			marginRight: theme.spacing(0.5)
		}
	},
	titleContainer: {
		display: "flex",
		flexDirection: "row",
		gap: theme.spacing(1),
		marginTop: theme.spacing(1),
		marginBottom: theme.spacing(4)
	},
	statusContainer: {
		marginLeft: "auto",
		flexShrink: 0,
		display: "flex",
		flexDirection: "row",
		alignItems: "center",
		gap: theme.spacing(1)
	}
}));

/** Quiz admin header — same layout as legacy AdminHeader for quiz flows, without shapeshift entity types. */
export interface QuizAdminHeaderProps {
	rootSlug: string;
	rootBreadcrumb: ReactNode;
	editMode: "edit" | "create";
	/** Create mode: main heading (e.g. "Create quiz"). Edit mode: unused; entity title is shown. */
	title: string;
	quiz?: { id: number; title?: string | null; publishedAt?: string | null } | null;
	actionButtons?: (null | ({ label: string; loading?: boolean } & Omit<ButtonProps, "label" | "children">))[];
}

export const QuizAdminHeader: FC<QuizAdminHeaderProps> = ({
	rootBreadcrumb,
	editMode,
	quiz,
	rootSlug,
	title,
	actionButtons
}) => {
	const {classes} = useStyles();
	const filteredActionButtons = (actionButtons ?? []).filter(Boolean) as NonNullable<
	QuizAdminHeaderProps["actionButtons"]
	>;
	const showActions = filteredActionButtons.length > 0;
	const createHeading = editMode === "create" ? title?.trim() || "Create New quiz" : "";

	return (
		<Box className={classes.header}>
			<Breadcrumbs className={classes.breadcrumbs} separator="/" aria-label="breadcrumb">
				<Box component="span" sx={{display: "flex", alignItems: "center"}}>
					{rootBreadcrumb}
				</Box>
				{editMode === "create" ? (
					<Box component="span" sx={{display: "flex", alignItems: "center"}}>
						<AddIcon />
						<Typography>Create New</Typography>
					</Box>
				) : quiz ? (
					<Box component="span" sx={{display: "flex", alignItems: "center"}}>
						<PlayArrowIcon />
						<Link to={`${rootSlug}/${quiz.id}`}>
							<Typography>{quiz.title}</Typography>
						</Link>
					</Box>
				) : null}
			</Breadcrumbs>
			<Box className={classes.titleContainer}>
				<Box>
					{editMode === "create" ? (
						<Typography variant="h4" className={title?.trim() ? classes.title : classes.newTitle}>
							{createHeading}
						</Typography>
					) : (
						<Typography variant="h4" className={classes.title}>
							{quiz?.title}
						</Typography>
					)}
				</Box>
				{showActions ? (
					<Box className={classes.statusContainer}>
						{filteredActionButtons.map((btn, i) => {
							const {label, loading, ...buttonProps} = btn as {
								label: string;
								loading?: boolean;
							} & Omit<ButtonProps, "children">;
							return (
								<Button key={`action-${i}`} {...buttonProps}>
									{loading ? <CircularProgress size={20} color="inherit" /> : label}
								</Button>
							);
						})}
					</Box>
				) : null}
			</Box>
		</Box>
	);
};
